/**
 * Convierte el plano (JSON de terceros, no confiable) en un modelo plano y seguro:
 * arrays indexados por número de butaca, colores validados y textos recortados.
 * Nada de aquí toca el DOM.
 */

const HEX = /^#[0-9a-fA-F]{6}$/;
export const FALLBACK_COLOR = "#8a8f98";

export const safeColor = (c: unknown, fallback = FALLBACK_COLOR): string => (typeof c === "string" && HEX.test(c) ? c : fallback);

function lin(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** Tinta (negro o blanco) que mejor se lee sobre un color de categoría. */
export const inkFor = (hex: string): "#101014" | "#ffffff" => (contrast(hex, "#ffffff") >= contrast(hex, "#101014") ? "#ffffff" : "#101014");

export interface Cat {
  key: string;
  label: string;
  color: string;
  ink: string;
}
export interface SeatNode {
  id: string;
  label: string;
  x: number;
  y: number;
  cat: number;
  row: number;
  pos: number;
  accessible: boolean;
}
export interface RowNode {
  id: string;
  label: string;
  sectionId: string;
  sectionLabel: string;
  cat: number;
  seats: number[];
}
export interface ZoneNode {
  id: string;
  label: string;
  cat: number;
  capacity: number;
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface ChartModel {
  name: string;
  width: number;
  height: number;
  cats: Cat[];
  catIndex: Map<string, number>;
  seats: SeatNode[];
  rows: RowNode[];
  zones: ZoneNode[];
  stage: { label: string; x: number; y: number; w: number; h: number } | null;
  sections: Array<{ id: string; label: string; x: number; y: number }>;
  index: Map<string, number>;
  zoneIndex: Map<string, number>;
  /** distancia típica entre butacas contiguas (unidades del plano) */
  pitch: number;
  /** radio de dibujo de una butaca */
  radius: number;
}

const str = (v: unknown, max = 80): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "").slice(0, max);
const num = (v: unknown, d = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : d);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const rec = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** Mediana de la distancia entre butacas consecutivas de una misma fila. */
export function estimatePitch(rows: Array<{ seats: number[] }>, xs: ArrayLike<number>, ys: ArrayLike<number>): number {
  const d: number[] = [];
  for (const r of rows) {
    for (let i = 1; i < r.seats.length; i++) {
      const a = r.seats[i - 1]!;
      const b = r.seats[i]!;
      const dist = Math.hypot(xs[a]! - xs[b]!, ys[a]! - ys[b]!);
      if (dist > 0.01) d.push(dist);
    }
  }
  if (d.length === 0) return 24;
  d.sort((p, q) => p - q);
  return d[Math.floor(d.length / 2)]!;
}

export function buildModel(raw: unknown): ChartModel {
  const o = rec(raw);
  const vb = rec(o.viewBox);
  const cats: Cat[] = [];
  const catIndex = new Map<string, number>();
  for (const c of arr(o.categories)) {
    const k = rec(c);
    const key = str(k.key, 48);
    if (!key || catIndex.has(key)) continue;
    const color = safeColor(k.color);
    catIndex.set(key, cats.length);
    cats.push({ key, label: str(k.label, 40) || key, color, ink: inkFor(color) });
  }
  if (cats.length === 0) {
    cats.push({ key: "_", label: "", color: FALLBACK_COLOR, ink: inkFor(FALLBACK_COLOR) });
    catIndex.set("_", 0);
  }
  const catOf = (key: unknown): number => catIndex.get(str(key, 48)) ?? 0;

  const sections = arr(o.sections).map((s) => {
    const r = rec(s);
    return { id: str(r.id, 48), label: str(r.label, 40), x: num(r.x), y: num(r.y) };
  });
  const sectionLabel = new Map(sections.map((s) => [s.id, s.label] as const));

  const seats: SeatNode[] = [];
  const rows: RowNode[] = [];
  const index = new Map<string, number>();
  for (const r0 of arr(o.rows)) {
    const r = rec(r0);
    const rowIdx = rows.length;
    const row: RowNode = {
      id: str(r.id, 48),
      label: str(r.label, 16),
      sectionId: str(r.section, 48),
      sectionLabel: sectionLabel.get(str(r.section, 48)) || str(r.section, 48),
      cat: catOf(r.category),
      seats: [],
    };
    for (const s0 of arr(r.seats)) {
      const s = rec(s0);
      const id = str(s.id, 64);
      if (!id || index.has(id)) continue;
      const i = seats.length;
      index.set(id, i);
      row.seats.push(i);
      seats.push({ id, label: str(s.label, 12) || String(row.seats.length), x: num(s.x), y: num(s.y), cat: row.cat, row: rowIdx, pos: row.seats.length - 1, accessible: s.accessible === true });
    }
    if (row.seats.length) rows.push(row);
  }

  const zones: ZoneNode[] = [];
  const zoneIndex = new Map<string, number>();
  for (const z0 of arr(o.zones)) {
    const z = rec(z0);
    const id = str(z.id, 48);
    if (!id || zoneIndex.has(id)) continue;
    zoneIndex.set(id, zones.length);
    zones.push({
      id,
      label: str(z.label, 40) || id,
      cat: catOf(z.category),
      capacity: Math.max(0, Math.floor(num(z.capacity))),
      x: num(z.x),
      y: num(z.y),
      w: Math.max(1, num(z.w, 40)),
      h: Math.max(1, num(z.h, 20)),
    });
  }

  const st = o.stage === undefined ? null : rec(o.stage);
  const stage = st ? { label: str(st.label, 40), x: num(st.x), y: num(st.y), w: Math.max(1, num(st.w, 100)), h: Math.max(1, num(st.h, 30)) } : null;

  const xs = seats.map((s) => s.x);
  const ys = seats.map((s) => s.y);
  const pitch = Math.max(2, estimatePitch(rows, xs, ys));

  // viewBox declarado, o bien el rectángulo que envuelve todo el contenido.
  let width = num(vb.w);
  let height = num(vb.h);
  if (!(width > 0) || !(height > 0)) {
    let maxX = 100;
    let maxY = 100;
    for (const s of seats) {
      maxX = Math.max(maxX, s.x + pitch);
      maxY = Math.max(maxY, s.y + pitch);
    }
    for (const z of zones) {
      maxX = Math.max(maxX, z.x + z.w);
      maxY = Math.max(maxY, z.y + z.h);
    }
    width = maxX;
    height = maxY;
  }

  return {
    name: str(o.name, 120),
    width,
    height,
    cats,
    catIndex,
    seats,
    rows,
    zones,
    stage,
    sections,
    index,
    zoneIndex,
    pitch,
    radius: Math.min(20, Math.max(2, pitch * 0.38)),
  };
}
